/**
 * <plw-fit-text> — scales its inner text so it fills the element's width on a
 * single line, floored at `data-min` (rem). By default it GROWS unbounded to
 * fit the container (CQ-2095 follow-up — page width is uncapped, so the
 * display titles keep growing with it); `data-cap` restores the `data-max`
 * ceiling (used by the basic-page titles, which are deliberately small).
 * `data-max` always remains the measuring reference size.
 *
 * Pixel-perfect: measures the text at the max size, then scales font-size by
 * container-width / text-width. Re-fits on container resize and after web fonts
 * load. It never fits with fallback-font metrics (the first fit waits for the
 * brand face via document.fonts.load) — the CSS estimate (base.css, from the
 * snippet's per-glyph `--fit-em`) holds the box until then, so the content
 * below the title doesn't snap when the exact fit lands. While scripting is
 * enabled the text stays invisible (base.css `@media (scripting: enabled)`)
 * until the first fit computed with final font metrics stamps `data-fitted` —
 * so neither the CSS estimate nor a fallback-font fit ever paints (CQ-2095).
 *
 * Markup:
 *   <plw-fit-text data-max="20" data-min="3">
 *     <span class="plw-fit-text__inner">Glossary</span>
 *   </plw-fit-text>
 */
class PlwFitText extends HTMLElement {
  #rafId = 0;

  connectedCallback() {
    this.inner = this.querySelector('.plw-fit-text__inner');
    if (!this.inner) return;

    this.scheduleFit = this.scheduleFit.bind(this);
    this.resizeObserver = new ResizeObserver(this.scheduleFit);
    this.resizeObserver.observe(this);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.scheduleFit);
    }
    this.fit();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /**
   * Coalesce ResizeObserver/font-load callbacks into a single rAF so the
   * read→write reflow runs once per frame instead of synchronously per event.
   */
  scheduleFit() {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.fit();
    });
  }

  /** @type {boolean} */
  #awaitingFace = false;

  fit() {
    const inner = this.inner;
    if (!inner) return;

    // Never fit with fallback-font metrics: a fallback fit lands a wrong size
    // (≈15% off for Signifier vs Georgia) that the fonts.ready refit then
    // corrects — a visible jump of everything below the title. Until the
    // brand face is loaded, leave the CSS estimate (already within a few px)
    // in place and fit once, when the face arrives.
    if (document.fonts && typeof document.fonts.check === 'function') {
      const style = getComputedStyle(inner);
      // Primary family only: the stack's metric-override fallback faces
      // (`Signifier Fallback`, local Georgia) stay "unloaded" forever, which
      // would make check() on the full stack false even after Signifier is in.
      const family = style.fontFamily.split(',')[0].trim();
      const face = `${style.fontStyle} ${style.fontWeight} 16px ${family}`;
      if (!document.fonts.check(face)) {
        if (!this.#awaitingFace) {
          this.#awaitingFace = true;
          document.fonts.load(face).then(
            () => {
              this.#awaitingFace = false;
              this.scheduleFit();
            },
            () => {
              // Face failed — fit with whatever renders so the text isn't stuck hidden.
              this.#awaitingFace = false;
              this.#fitNow();
            }
          );
        }
        return;
      }
    }

    this.#fitNow();
  }

  #fitNow() {
    const inner = this.inner;
    if (!inner) return;

    const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const max = (parseFloat(this.dataset.max) || 12) * rootSize;
    const min = (parseFloat(this.dataset.min) || 1) * rootSize;
    const containerWidth = this.clientWidth;
    if (!containerWidth) return;

    // Measure the natural text width at the max size, then scale to fit.
    inner.style.fontSize = `${max}px`;
    const textWidth = inner.scrollWidth;
    if (!textWidth) return;

    let size = Math.max(min, (max * containerWidth) / textWidth);
    if (this.hasAttribute('data-cap')) size = Math.min(max, size);
    inner.style.fontSize = `${size}px`;

    // Reveal once this fit used final glyph widths — i.e. the web fonts are
    // done (the fonts.ready handler re-fits and lands here). Browsers without
    // the Font Loading API just reveal on the first fit.
    if (!document.fonts || document.fonts.status === 'loaded') {
      this.setAttribute('data-fitted', '');
    }
  }
}

if (!customElements.get('plw-fit-text')) {
  customElements.define('plw-fit-text', PlwFitText);
}
